import React, {useEffect, useState} from "react";
import moment from "moment";

const Calendar: React.FC<{
    selectedDate: string;
    onDateSelect: (date: string) => void;
    availableDays: string[];
}> = ({ selectedDate, onDateSelect, availableDays }) => {
    const [currentMonth, setCurrentMonth] = useState(moment());

    moment.updateLocale('en', {
        week : {
            dow : 1
        }
    });

    const prevMonth = () => {
        setCurrentMonth(currentMonth.clone().subtract(1, "months"));
    }
    const nextMonth = () => {
        setCurrentMonth(currentMonth.clone().add(1, "months"))
    }

    const renderHeader = () => {
        const thisMonth = moment().startOf("month");
        return (
            <div className="header">
                <h5>{currentMonth.format("MMMM")} <span className={"year"}>{currentMonth.format("YYYY")}</span></h5>
                <div>
                    <button onClick={prevMonth}
                            disabled={currentMonth.isSame(thisMonth, "month")}>{renderArrowLeft()}</button>
                    <button onClick={nextMonth}>{renderArrowRight()}</button>
                </div>
            </div>
        )
    };

    const renderArrowLeft = () => {
        return <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M15.5307 19.2545C15.6004 19.3241 15.6557 19.4069 15.6934 19.4979C15.7311 19.589 15.7505 19.6865 15.7505 19.7851C15.7505 19.8836 15.7311 19.9812 15.6934 20.0723C15.6557 20.1633 15.6004 20.246 15.5307 20.3157C15.461 20.3854 15.3783 20.4407 15.2873 20.4784C15.1962 20.5161 15.0986 20.5355 15.0001 20.5355C14.9016 20.5355 14.804 20.5161 14.7129 20.4784C14.6219 20.4407 14.5392 20.3854 14.4695 20.3157L6.96948 12.8157C6.89974 12.7461 6.84443 12.6633 6.80668 12.5723C6.76894 12.4812 6.74951 12.3836 6.74951 12.2851C6.74951 12.1865 6.76894 12.0889 6.80668 11.9979C6.84443 11.9068 6.89974 11.8241 6.96948 11.7545L14.4695 4.25446C14.6102 4.11373 14.8011 4.03467 15.0001 4.03467C15.1991 4.03467 15.39 4.11373 15.5307 4.25446C15.6715 4.39519 15.7505 4.58606 15.7505 4.78508C15.7505 4.98411 15.6715 5.17498 15.5307 5.31571L8.56041 12.2851L15.5307 19.2545Z"
                fill="currentColor"
            />
        </svg>

    };

    const renderArrowRight = () => {
        return <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M17.0306 12.8157L9.53055 20.3157C9.46087 20.3854 9.37815 20.4407 9.2871 20.4784C9.19606 20.5161 9.09847 20.5355 8.99993 20.5355C8.90138 20.5355 8.8038 20.5161 8.71276 20.4784C8.62171 20.4407 8.53899 20.3854 8.4693 20.3157C8.39962 20.246 8.34435 20.1633 8.30663 20.0723C8.26892 19.9812 8.24951 19.8836 8.24951 19.7851C8.24951 19.6865 8.26892 19.589 8.30663 19.4979C8.34435 19.4069 8.39962 19.3241 8.4693 19.2545L15.4396 12.2851L8.4693 5.31571C8.32857 5.17498 8.24951 4.98411 8.24951 4.78508C8.24951 4.58606 8.32857 4.39519 8.4693 4.25446C8.61003 4.11373 8.80091 4.03467 8.99993 4.03467C9.19895 4.03467 9.38982 4.11373 9.53055 4.25446L17.0306 11.7545C17.1003 11.8241 17.1556 11.9068 17.1933 11.9979C17.2311 12.0889 17.2505 12.1865 17.2505 12.2851C17.2505 12.3836 17.2311 12.4812 17.1933 12.5723C17.1556 12.6633 17.1003 12.7461 17.0306 12.8157Z"
                fill="currentColor"
            />
        </svg>
    }

    const renderDays = () => {
        const days = [];
        const start = moment().startOf("isoWeek");
        for (let i = 0; i < 7; i++) {
            days.push(
                <div key={i} className={"cell day-in-week" + (i > 4 ? " not-available" : "")}>
                    {start.clone().add(i, "days").format("ddd")}
                </div>
            );
        }
        return days;
    };

    const renderCells = () => {
        const start = currentMonth.clone().startOf("month").startOf("isoWeek");
        const end = currentMonth.clone().endOf("month").endOf("isoWeek");
        const rows = [];
        let days = [];
        let day = start.clone();
        const today = moment().startOf("day");

        while (day.isBefore(end)) {
            for (let i = 0; i < 7; i++) {
                const formattedDate = day.format("YYYY-MM-DD");
                const disabled = !availableDays.includes(formattedDate) || day.isBefore(today);
                const isSelected = selectedDate === formattedDate;

                days.push(
                    <div
                        key={day.toString()}
                        className={`cell ${
                            !day.isSame(currentMonth, "month")
                                ? "another-month"
                                : disabled
                                ? "not-available"
                                : isSelected
                                    ? "selected"
                                    : "available"
                        }`}
                        onClick={() => !disabled && !day.isSame(currentMonth, "month") && onDateSelect(formattedDate)}
                    >
                        {day.date()}
                    </div>
                );
                day.add(1, "day");
            }

        }
        return days;
    };

    return (
        <div className={"calendar-wrapper"}>
            {renderHeader()}
            <div className={"calendar"}>
                <div className="grid">{renderDays()}</div>
                <div className="grid">{renderCells()}</div>
            </div>
        </div>
    );
};

export default Calendar;
